import React, { useState, useEffect } from 'react';
import styles from '../styles/CountdownTimer.module.css';

interface CountdownTimerProps {
  endDate: string;
  heading?: string;
  subheading?: string;
  buttonText?: string;
  buttonLink?: string;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({
  endDate,
  heading = 'Hurry up!',
  subheading = 'Sale end in:',
  buttonText = 'Shop now!',
  buttonLink = '#'
}) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    // Calculate end date - use today + 1 day if not provided or invalid
    const targetDate = endDate ? new Date(endDate) : new Date(Date.now() + 24 * 60 * 60 * 1000);
    
    // If date is invalid, use tomorrow
    const validTargetDate = isNaN(targetDate.getTime()) 
      ? new Date(Date.now() + 24 * 60 * 60 * 1000) 
      : targetDate;

    const calculateTimeLeft = () => {
      const difference = validTargetDate.getTime() - new Date().getTime();
      
      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((difference / 1000 / 60) % 60);
        const seconds = Math.floor((difference / 1000) % 60);
        
        setTimeLeft({ days, hours, minutes, seconds });
      } else {
        // If countdown is complete, show all zeros
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    // Calculate immediately
    calculateTimeLeft();
    
    // Update every second
    const timer = setInterval(calculateTimeLeft, 1000);
    
    // Cleanup
    return () => clearInterval(timer);
  }, [endDate]);

  // Format numbers to always show two digits (e.g., 01, 02, etc.)
  const formatNumber = (num: number) => {
    return num.toString().padStart(2, '0');
  };
  
  return (
    <div className={styles.countdownTimer}>
      <h2 className={styles.heading}>{heading}</h2>
      <p className={styles.subheading}>{subheading}</p>
      
      <div className={styles.timerContainer}>
        <div className={styles.timeUnit}>
          <div className={styles.timeValue}>{formatNumber(timeLeft.days)}</div>
          <div className={styles.timeLabel}>Days</div>
        </div>
        <div className={styles.separator}>:</div>
        <div className={styles.timeUnit}>
          <div className={styles.timeValue}>{formatNumber(timeLeft.hours)}</div>
          <div className={styles.timeLabel}>Hrs</div>
        </div>
        <div className={styles.separator}>:</div>
        <div className={styles.timeUnit}>
          <div className={styles.timeValue}>{formatNumber(timeLeft.minutes)}</div>
          <div className={styles.timeLabel}>Mins</div>
        </div>
        <div className={styles.separator}>:</div>
        <div className={styles.timeUnit}>
          <div className={styles.timeValue}>{formatNumber(timeLeft.seconds)}</div>
          <div className={styles.timeLabel}>Secs</div>
        </div>
      </div>
      
      {buttonText && (
        <a href={buttonLink} className={styles.countdownButton}>
          {buttonText}
        </a>
      )}
    </div>
  );
};

export default CountdownTimer; 